// Ui stuff
import { toast } from 'svelte-sonner';

// Phidgets
import * as phidget22 from '$lib/phidgets/phidget22.esm.min.js';

// RoastLog
import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { TCType, type Nullable } from '$lib/types/RoastLog';
import type { ConnectOptions } from '..';
import { BaseDevice } from '../base';

class PhidgetsDevice extends BaseDevice {
  type: DeviceType = DeviceType.PHIDGETS;
  private phidgetSensors: Nullable<phidget22.TemperatureSensor>[] = [];

  protected async onConnect(options: ConnectOptions): Promise<boolean> {
    try {
      this.phidgetSensors = await this.phidgetsConnect(options);

      const allNull = this.phidgetSensors.every((sensor) => sensor === null);
      if (allNull) {
        toast.error(`Could not connect to Phidgets. Please check that the
          Phidgets Network Server is running and that thermocouples are
          plugged in to .`);
        return false;
      }

      return true;
    } catch (error) {
      logger.error(`Phidgets connection error: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    this.phidgetSensors.forEach((sensor) => {
      if (sensor && sensor.isOpen) {
        logger.info(`Closing phidget sensor ${sensor.channel}`);
        sensor.close();
      }
    });
  }

  protected onDetach = (p: phidget22.TemperatureSensor) => {
    if (p.isOpen) {
      logger.warn(`Closing phidget sensor from detach ${p.channel}`);
      p.close();
    }
    const anythingOpen = this.phidgetSensors.map((s) => s?.isOpen).filter((s) => s === true);
    if (anythingOpen.length === 0) {
      toast.error(`Phidgets disconnected. Shutting down.`);
      this.disconnect();
    }
  };

  protected async updateReadings(): Promise<void> {
    // No need to do anything here as the readings are updated via callbacks
    // from the Phidgets library
  }

  private async phidgetsConnect({
    tcSettings
  }: ConnectOptions): Promise<(phidget22.TemperatureSensor | null)[]> {
    try {
      const port = 8989;
      const conn = new phidget22.Connection(port, 'localhost');

      logger.info('Connecting to phidgets...');
      await conn.connect();
      logger.info('Connected to phidgets!');

      const enabledTcs = [true, tcSettings[1].active, tcSettings[2].active, tcSettings[3].active];

      // Wait for all sensors to be initialized
      const tempSensors = await Promise.all(
        enabledTcs.map(async (enabled, index) => {
          if (!enabled) {
            return null;
          }

          const tempSensor = new phidget22.TemperatureSensor();
          tempSensor.channel = index;

          tempSensor.onTemperatureChange = (temperature: number) => {
            this.localReadings[index] = temperature;
          };

          tempSensor.onError = (e: number) => {
            if (e === 4105) {
              toast.error(
                `Phidgets error. TC ${tempSensor.channel + 1} is enabled but a thermocouple is not attached. Disable this channel your settings.`
              );
              tempSensor.close();
              // Automatically disable that channel??
            }
          };

          tempSensor.onDetach = (p: phidget22.TemperatureSensor) => {
            this.onDetach(p);
          };

          tempSensor.onPropertyChange = (p: any) => {
            logger.info(`Phidgets Property Change: ${p}`);
          };

          const tcType =
            tcSettings[index].type === TCType.J
              ? phidget22.ThermocoupleType.J
              : phidget22.ThermocoupleType.K;
          tempSensor.onAttach = () => {
            logger.info(
              `Phidgets attached at channel ${tempSensor.channel}, ${tempSensor.deviceID}`
            );
            tempSensor.setDataInterval(500);
            tempSensor.setThermocoupleType(tcType);
          };

          try {
            await tempSensor.open(5000);
            return tempSensor;
          } catch (error) {
            logger.error(`Phidgets connection error at channel ${tempSensor.channel}: ${error}`);
            return null;
          }
        })
      );

      return tempSensors;
    } catch (error) {
      logger.error(`Phidgets connection error: ${error}`);
      return Array(4).fill(null); // Return array of nulls if connection fails
    }
  }
}

export default PhidgetsDevice;
